import { DatePicker, Input, Radio, Select, Space } from "antd";
import dayjs, { Dayjs } from "dayjs";
import React, { useState } from "react";
import { formatPhoneInputValue } from "../../../../../../../../../../../../utils/inputUtils";
import { AgentDetailFormFieldType } from "../../agentDetailFormField.types";
import styles from "./agentDetailFormInput.module.css";
import { DateFormat } from "../../../../../../../../../../../../constants/dateFormat";

export interface AgentDetailFormInputProps {
    type: AgentDetailFormFieldType;
    value: string | null;
    placeholder?: string;
    disabled?: boolean;
    addonField?: string | null;
    options?: { label: string; value: string }[];
    loading?: boolean;
    filter?: (value: string) => string;
    onChange: (value: string | null) => void;
}
const AgentDetailDateInput = ({
    value,
    placeholder,
    disabled = true,
    onChange,
}: Pick<AgentDetailFormInputProps, "value" | "placeholder" | "disabled" | "onChange">) => {
    const [resetKey, setResetKey] = useState(0);

    const handleChange = (date: Dayjs | null) => {
        if (!date) {
            onChange(null);
            return;
        }
        if (date.isAfter(dayjs(), "day")) {
            setResetKey((key) => key + 1);
            return;
        }
        onChange(date.format(DateFormat.YYYY_MM_DD));
    };

    return (
        <DatePicker
            key={`${value ?? "empty"}-${resetKey}`}
            className={styles.datePicker}
            value={value ? dayjs(value) : null}
            onChange={handleChange}
            onBlur={() => {
                // Defer so a valid mask commit can update `value` first; remount then clears any drifted text.
                window.setTimeout(() => setResetKey((key) => key + 1), 0);
            }}
            format={{
                format: "MM/DD/YYYY",
                type: "mask",
            }}
            placeholder={placeholder}
            disabled={disabled}
            open={disabled ? false : undefined}
            allowClear={!disabled}
            maxDate={dayjs()}
        />
    );
};

export const AgentDetailFormInput = ({
    type,
    value,
    placeholder,
    disabled = true,
    addonField,
    options,
    loading,
    filter,
    onChange,
}: AgentDetailFormInputProps) => {
    const defaultValue = disabled ? "-" : "";
    switch (type) {
        case "date":
            return (
                <AgentDetailDateInput
                    value={value}
                    placeholder={placeholder}
                    disabled={disabled}
                    onChange={onChange}
                />
            );

        case "tel":
            return (
                <Input
                    className={styles.input}
                    value={value || defaultValue}
                    onChange={(event) =>
                        onChange(formatPhoneInputValue(event.target.value) || null)
                    }
                    placeholder={placeholder}
                    disabled={disabled}
                    allowClear={!disabled}
                    maxLength={12}
                    inputMode="numeric"
                />
            );

        case "email":
            return (
                <Input
                    className={styles.input}
                    value={value || defaultValue}
                    disabled={disabled}
                    onChange={(e) => onChange(e.target.value || null)}
                    placeholder={placeholder}
                    allowClear={!disabled}
                    type="email"
                />
            );

        case "website":
            return (
                <Space.Compact className={styles.websiteInput} block>
                    <Input
                        className={styles.websiteFieldInput}
                        value={value || defaultValue}
                        disabled={disabled}
                        onChange={(e) => {
                            const raw = e.target.value;
                            onChange((filter ? filter(raw) : raw) || null);
                        }}
                        placeholder={placeholder}
                        allowClear={!disabled}
                    />
                    {addonField && (
                        <Space.Addon className={styles.websiteAddon}>.{addonField}</Space.Addon>
                    )}
                </Space.Compact>
            );

        case "select":
            return (
                <Select
                    className={styles.select}
                    value={value ?? (disabled ? "-" : undefined)}
                    placeholder={placeholder}
                    disabled={disabled}
                    options={options}
                    loading={loading}
                    onChange={(nextValue) => onChange(nextValue ?? null)}
                    open={disabled ? false : undefined}
                    showSearch={!disabled}
                    optionFilterProp="label"
                    allowClear={!disabled}
                />
            );

        default:
            return (
                <Input
                    className={styles.input}
                    value={value || defaultValue}
                    disabled={disabled}
                    onChange={(e) => {
                        const raw = e.target.value;
                        onChange((filter ? filter(raw) : raw) || null);
                    }}
                    placeholder={placeholder}
                    allowClear={!disabled}
                />
            );
    }
};

export const AccountStatusRadio = ({
    isActive,
    disabled = true,
    onChange,
}: {
    isActive: boolean | null;
    disabled?: boolean;
    onChange?: (isActive: boolean) => void;
}) => (
    <Radio.Group
        options={[
            { label: "Active", value: "active" },
            { label: "Inactive", value: "inactive" },
        ]}
        value={isActive === true ? "active" : "inactive"}
        disabled={disabled}
        onChange={(e) => onChange?.(e.target.value === "active")}
    />
);
